<template>

    <div class="row my-2">
        <div class="col">
            <kt-input 
                v-model="model.TechnicianName" 
                label="Technician Name" 
                placeholder="Enter technician name"
            ></kt-input>
        </div>
    </div>

    <div class="row my-2">
        <div class="col">
            <kt-input 
                v-model="model.Email" 
                label="Email" 
                placeholder="Enter email address"
            ></kt-input>
        </div>
    </div>

    <div class="row my-2">
        <div class="col">
            <kt-input 
                type="password" 
                v-model="model.Password" 
                label="Password" 
                placeholder="Leave blank to keep the same"
            ></kt-input>
        </div>
    </div>

    <div class="row my-2">
        <div class="col">
            <div class="form-check">
                <input 
                    type="checkbox" 
                    class="form-check-input" 
                    v-model="model.Active" 
                    id="active-check"
                >
                <label class="form-check-label" 
                       for="active-check">
                    Is Active?
                </label>
            </div>
        </div>
    </div>

    <!-- Profile Image Section -->
    <div class="row mt-4" v-if="model.Id">
        <div class="col">
            <h6 class="text-muted mb-2">Profile Image</h6>
            
            <!-- Loading state -->
            <div v-if="imageLoading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
                <p class="text-muted mt-2">Loading image...</p>
            </div>
            
            <!-- Display existing image -->
            <div v-else-if="profileImage" class="mb-3">
                <img 
                    :src="imageUrl" 
                    alt="Profile Image"
                    class="img-thumbnail"
                    style="max-width: 200px; max-height: 200px; object-fit: cover;"
                />
                <div class="mt-2">
                    <button 
                        @click="deleteImage()" 
                        class="btn btn-sm btn-outline-danger me-2"
                    >
                        <i class="fa fa-trash"></i> Delete Image
                    </button>
                    <label class="btn btn-sm btn-outline-primary">
                        <i class="fa fa-upload"></i> Replace Image
                        <input 
                            type="file" 
                            @change="handleImageUpload($event)" 
                            accept="image/*"
                            style="display: none;"
                        />
                    </label>
                </div>
            </div>

            <!-- Upload new image -->
            <div v-else>
                <label class="btn btn-sm btn-outline-primary">
                    <i class="fa fa-upload"></i> Upload Profile Image
                    <input 
                        type="file" 
                        @change="handleImageUpload($event)" 
                        accept="image/*"
                        style="display: none;"
                    />
                </label>
                <p class="text-muted small mt-2">Supported formats: JPG, PNG, GIF (max 5MB)</p>
            </div>
        </div>
    </div>
    
</template>

<script lang="ts" setup>

//
// TechForm.vue
// @version 1.0.0
//

import { ref, onMounted, defineModel, watch, computed } from "vue";
import KtInput from "@/components/UI/KtInput.vue";
import { TechnicianModel } from "@/models/TechnicianModel";
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
import { getApiBaseUrl } from "@/services/Http";

const model = defineModel({
    type: TechnicianModel,
    default: new TechnicianModel()
});

const profileImage = ref<any>(null);
const imageLoading = ref(false);
const imageUrl = computed(() => {
    return profileImage.value ? `${getApiBaseUrl()}/Data/Download/${profileImage.value.Id}` : '';
});

// Load profile image when model.Id changes
watch(() => model.value.Id, async (newId) => {
    if (newId) {
        await loadProfileImage();
    }
}, { immediate: true });

const loadProfileImage = async () => {
    if (!model.value.Id || model.value.Id === undefined) return;
    
    imageLoading.value = true;
    try {
        const response = await ApiService.loadTechnicianImage(model.value.Id);
        profileImage.value = response || null;
    } catch (error) {
        console.error('Error loading profile image:', error);
        profileImage.value = null;
    } finally {
        imageLoading.value = false;
    }
};

const handleImageUpload = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        Alert.notify({
            text: 'Image size must be less than 5MB',
            type: 'error'
        });
        return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
        Alert.notify({
            text: 'Please select a valid image file',
            type: 'error'
        });
        return;
    }

    try {
        const response = await ApiService.uploadProfileImage(file, 'technician', model.value.Id!);
        profileImage.value = response;
        Alert.notify({
            text: 'Profile image uploaded successfully!',
            type: 'success'
        });
    } catch (error) {
        console.error('Error uploading image:', error);
        Alert.notify({
            text: 'Error uploading image',
            type: 'error'
        });
    }

    // Reset file input
    target.value = '';
};

const deleteImage = async () => {
    if (!profileImage.value) return;

    const imageId = profileImage.value.Id;
    Alert.confirm('Are you sure you want to delete this profile image?', async () => {
        try {
            await ApiService.deleteProfileImage(imageId);
            profileImage.value = null;
            Alert.notify({
                text: 'Profile image deleted successfully!',
                type: 'success'
            });
        } catch (error) {
            console.error('Error deleting image:', error);
            Alert.notify({
                text: 'Error deleting image',
                type: 'error'
            });
        }
    });
};

onMounted(async () =>
{
    // -- ...
});

</script>